import type { CopyStore, KeptCopy } from "./store";
import { keyOf } from "./store";
import type { SystemClipboard } from "./system";

/**
 * One clipboard for the person across every Orthos tab and window of the origin, as the Windows clipboard is in
 * AutoCAD. A copy in any drawing (COPYCLIP, CUTCLIP, COPYBASE, Ctrl+C) comes from the tab's engine as `copied`:
 *
 * - its summary goes to the system clipboard as plain text, for other apps;
 * - its share form goes to every other tab on a BroadcastChannel, and into a per-origin store, so a reload and tabs
 *   opened later have it too. A copy too big to share (the engine said so on the command line) stays in its tab.
 *
 * Every copy is keyed by the person and their organisation: a tab hears only its own person's copies in its own
 * organisation, a tab's engine for another person or organisation starts without them, and a sign-out (or another
 * person signing in) forgets them all, in every tab.
 */

/** What the tab clipboard tells its engine (`DraftEngine`). */
export interface ClipboardEngine {
  /** A copy from another tab or the store: every open drawing's clipboard. */
  clipboardIn(payload: string): void;
  /** Empties every open drawing's clipboard. */
  clipboardClear(): void;
}

/** A copy as the engine posts it (`FromWorker` `copied`). */
export interface EngineCopy {
  readonly payload?: string | undefined;
  readonly text: string;
}

/** The tabs' messages to each other. */
export type ClipboardMessage =
  { readonly kind: "copy"; readonly copy: KeptCopy } | { readonly kind: "clear" };

/** A BroadcastChannel, as the tab clipboard uses one. */
export interface ClipboardChannel {
  postMessage(message: ClipboardMessage): void;
  addEventListener(type: "message", listener: (event: MessageEvent<unknown>) => void): void;
}

export interface TabClipboardDeps {
  readonly store: CopyStore;
  /** `undefined` where the browser has no BroadcastChannel: copies then reach other tabs on their next start. */
  readonly channel: ClipboardChannel | undefined;
  readonly system: SystemClipboard;
  readonly now: () => number;
  /** Where a store that fails says so; the clipboard goes on within the tab. */
  readonly warn: (message: string, error: unknown) => void;
}

/** The latest copy this tab knows of: its own or another tab's. */
interface Latest {
  readonly stamp: number;
  readonly text: string;
  readonly onSystem: boolean;
}

function isMessage(value: unknown): value is ClipboardMessage {
  if (typeof value !== "object" || value === null || !("kind" in value)) return false;
  if (value.kind === "clear") return true;
  return (
    value.kind === "copy" &&
    "copy" in value &&
    typeof value.copy === "object" &&
    value.copy !== null
  );
}

export class TabClipboard {
  private readonly deps: TabClipboardDeps;
  private engine: ClipboardEngine | undefined;
  private who: { readonly user: string; readonly key: string } | undefined;
  private latest: Latest | undefined;

  constructor(deps: TabClipboardDeps) {
    this.deps = deps;
    deps.channel?.addEventListener("message", (event) => this.heard(event.data));
  }

  /**
   * The tab's engine, started for `user` in `organisation`: it gets that person's latest copy in that organisation,
   * from before a reload or from a tab that copied before this one opened. Copies of anyone else are forgotten.
   */
  attach(engine: ClipboardEngine, user: string, organisation: string): void {
    const key = keyOf(user, organisation);
    if (this.who?.key === key && this.engine === engine) return;
    // Another person or organisation in the same engine: what the last one copied goes first.
    if (this.who !== undefined && this.who.key !== key) {
      this.latest = undefined;
      if (this.engine === engine) engine.clipboardClear();
    }
    this.engine = engine;
    this.who = { user, key };
    void this.restore(engine, user, key);
  }

  /** The engine copied: to the system clipboard, the other tabs and the store. */
  async copied(copy: EngineCopy): Promise<void> {
    const who = this.who;
    const stamp = this.deps.now();
    this.latest = { stamp, text: copy.text, onSystem: false };
    const onSystem = await this.deps.system.write(copy.text);
    // Unless another copy (or a sign-out) came meanwhile.
    if (this.latestStamp() === stamp) this.latest = { stamp, text: copy.text, onSystem };
    if (who === undefined || copy.payload === undefined) return;
    const kept: KeptCopy = {
      key: who.key,
      user: who.user,
      payload: copy.payload,
      text: copy.text,
      onSystem,
      stamp,
    };
    this.deps.channel?.postMessage({ kind: "copy", copy: kept });
    await this.deps.store
      .put(kept)
      .catch((error: unknown) => this.deps.warn("The copy was not kept", error));
  }

  /** Sign-out, or another person signed in: every tab's clipboard empties and nothing is kept. */
  async clear(): Promise<void> {
    this.forget();
    this.deps.channel?.postMessage({ kind: "clear" });
    await this.deps.store
      .clear()
      .catch((error: unknown) => this.deps.warn("Copies were not forgotten", error));
  }

  /**
   * Whether Ctrl+V should paste `text` from the system clipboard as text: it holds text, and no Orthos copy is newer.
   * An Orthos copy is newer while the system clipboard still holds what it wrote, or when it could not write at all.
   */
  pastesAsText(text: string | undefined): boolean {
    if (text === undefined || text.trim() === "") return false;
    const latest = this.latest;
    if (latest === undefined) return true;
    return latest.onSystem && text !== latest.text;
  }

  private latestStamp(): number | undefined {
    return this.latest?.stamp;
  }

  private async restore(engine: ClipboardEngine, user: string, key: string): Promise<void> {
    try {
      await this.deps.store.keepOnly(user);
      const kept = await this.deps.store.get(key);
      if (kept !== undefined && this.engine === engine) this.take(kept);
    } catch (error) {
      this.deps.warn("The kept copy could not be read", error);
    }
  }

  private heard(data: unknown): void {
    if (!isMessage(data)) return;
    if (data.kind === "clear") this.forget();
    else if (data.copy.key === this.who?.key) this.take(data.copy);
  }

  /** A copy from elsewhere, unless this tab knows a newer one. */
  private take(copy: KeptCopy): void {
    if (this.latest !== undefined && this.latest.stamp >= copy.stamp) return;
    this.latest = { stamp: copy.stamp, text: copy.text, onSystem: copy.onSystem };
    this.engine?.clipboardIn(copy.payload);
  }

  private forget(): void {
    this.latest = undefined;
    this.engine?.clipboardClear();
  }
}
